import ModelPicker from "@/shared/components/models/ModelPicker";
import { ListCard } from "@/shared/components/ListCard";
import { Row, SelectInput } from "@/shared/components/settings-atoms";
import { Slider } from "@/shared/components/ui/slider";
import { useModels } from "@/shared/api/models";
import { EFFORTS, MODEL_DEFAULT } from "@/shared/lib/effort";
import { FAST_MODEL_FILTER, fastSelectionOf, mainModelFilter, modelOf, selectFastModel, selectionOf, selectAgentModel } from "@/shared/lib/models";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";

import type { Agent, ModelSettings } from "@/shared/types";
import type { AnswerStyle, Effort } from "@/shared/types/agents";

// how answers are written: normal (null) unless set
const STYLE_OPTIONS = [
  { id: "normal", label: "Normal" },
  { id: "concise", label: "Concise · the answer, little else" },
  { id: "detailed", label: "Detailed · reasons, steps, examples" },
];

const EFFORT_OPTIONS = [
  { id: MODEL_DEFAULT, label: "Model default" },
  ...EFFORTS.map((e) => ({ id: e.id, label: `${e.label} · ${e.detail.toLowerCase()}` })),
];

/** A slider with its value beside it, sized to sit in a settings row. */
function Dial({ label, value, shown, min, max, step, disabled, onChange }: {
  label: string;
  value: number;
  shown: string;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className={`flex items-center gap-3 w-[240px] max-w-full ${disabled ? "opacity-40" : ""}`}>
      <Slider aria-label={label} value={[value]} disabled={disabled} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} className="flex-1" />
      <span className="w-[42px] shrink-0 text-right text-[12.5px] font-mono tabular-nums text-[var(--text-primary)]">{shown}</span>
    </div>
  );
}

// the model turns a message takes when the agent sets none (backend agents/business/snapshot.py)
const DEFAULT_MAX_STEPS = 25;

// TODO: one token for the 300px picker width (also SettingsPage, ChatKnowledge, Citation, ModelPicker)
// As wide as a picker's menu, so the menu opens right under it
const PICKER_WIDTH = "w-[300px] max-w-full";

/* The model and its parameters. */
export default function ModelSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const { data: modelCatalog } = useModels();
  const selectedModel = modelOf(selectionOf(agent), modelCatalog);
  const fastSelection = fastSelectionOf(agent);
  // beside a fast model the main one must be slow, so a fast main model leaves no room for one
  const noRoomForFast = selectedModel?.tier === "fast" && !fastSelection;
  const updateModelSettings = (patch: Partial<ModelSettings>) => onUpdateAgent((a) => ({ ...a, modelSettings: { ...a.modelSettings, ...patch } }));
  const fastAuto = agent.modelSettings.fastAuto ?? true;
  const noFastModel = (auto: boolean) => onUpdateAgent((a) => {
    const none = selectFastModel(a, null);
    return { ...none, modelSettings: { ...none.modelSettings, fastAuto: auto } };
  });
  return (
    <Section section="model" onHelp={onHelp}>
      <ListCard>
        <Row className="px-4" title="Model" desc="The model that runs this agent, and the key it runs on.">
          <div className={PICKER_WIDTH}>
            <ModelPicker value={selectionOf(agent)} only={mainModelFilter(agent)} onChange={(selection, model) => onUpdateAgent((a) => selectAgentModel(a, selection, model))} />
          </div>
        </Row>
        <Row
          className="px-4"
          title="Fast model"
          desc={noRoomForFast ? "The model above is already a fast model." : "Simple messages and lookups go here, and it reads long pages for the model above, which takes the rest."}
        >
          <div className={PICKER_WIDTH}>
            <ModelPicker
              value={fastSelection}
              only={FAST_MODEL_FILTER}
              none={[
                { label: "Automatic", detail: "Newest fast model, same key", chosen: fastAuto, onSelect: () => noFastModel(true) },
                { label: "Off", detail: "Every message goes to the model above", chosen: !fastAuto, onSelect: () => noFastModel(false) },
              ]}
              disabled={noRoomForFast}
              onChange={(selection) => onUpdateAgent((a) => selectFastModel(a, selection))}
            />
          </div>
        </Row>
        <Row
          className="px-4"
          title="Thinking"
          desc={selectedModel && !selectedModel.takesEffort ? "This model sets its own." : "How hard the model thinks before it answers. Lower answers sooner."}
        >
          <SelectInput
            className="w-[160px]"
            value={agent.modelSettings.effort ?? MODEL_DEFAULT}
            disabled={!!selectedModel && !selectedModel.takesEffort}
            options={EFFORT_OPTIONS}
            onChange={(value) => updateModelSettings({ effort: value === MODEL_DEFAULT ? null : (value as Effort) })}
          />
        </Row>
        <Row className="px-4" title="Answer style" desc="How its answers are written.">
          <SelectInput
            className="w-[240px]"
            value={agent.modelSettings.style ?? "normal"}
            options={STYLE_OPTIONS}
            onChange={(value) => updateModelSettings({ style: value === "normal" ? null : (value as AnswerStyle) })}
          />
        </Row>
        <Row
          className="px-4"
          title="Temperature"
          desc={selectedModel && !selectedModel.temperature ? "This model sets its own." : "Lower keeps replies focused, higher makes them more varied."}
        >
          <Dial
            label="Temperature"
            value={agent.modelSettings.temperature}
            shown={agent.modelSettings.temperature.toFixed(2)}
            disabled={!selectedModel?.temperature}
            min={selectedModel?.temperature?.min ?? 0}
            max={selectedModel?.temperature?.max ?? 1}
            step={0.05}
            onChange={(temperature) => updateModelSettings({ temperature })}
          />
        </Row>
        <Row className="px-4" title="Steps per message" desc="The most model turns one message may take. More lets it finish longer jobs, and costs more.">
          <Dial
            label="Steps per message"
            value={agent.modelSettings.maxSteps ?? DEFAULT_MAX_STEPS}
            shown={String(agent.modelSettings.maxSteps ?? DEFAULT_MAX_STEPS)}
            min={5}
            max={100}
            step={5}
            onChange={(maxSteps) => updateModelSettings({ maxSteps })}
          />
        </Row>
        <Row className="px-4" title="Max tokens" desc="The longest a single reply can be.">
          <Dial
            label="Max tokens"
            value={agent.modelSettings.maxTokens}
            shown={String(agent.modelSettings.maxTokens)}
            min={256}
            max={selectedModel?.maxOutputTokens ?? 8192}
            step={256}
            onChange={(maxTokens) => updateModelSettings({ maxTokens })}
          />
        </Row>
      </ListCard>
    </Section>
  );
}
